import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Lang = 'uz-Latn' | 'uz-Cyrl' | 'ru' | 'en';

export const SUPPORTED_LANGS: Lang[] = ['uz-Latn', 'uz-Cyrl', 'ru', 'en'];
export const DEFAULT_LANG: Lang = 'uz-Latn';
const STORAGE_KEY = 'lang';

export type Dict = Record<string, string>;
export type TVars = Record<string, string | number>;

interface LangContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, vars?: TVars) => string;
}

const LangContext = createContext<LangContextValue | null>(null);

const uzLatnModules = import.meta.glob('./locales/uz-Latn/*.json', { eager: true, import: 'default' }) as Record<string, Dict>;
const uzCyrlModules = import.meta.glob('./locales/uz-Cyrl/*.json', { eager: true, import: 'default' }) as Record<string, Dict>;
const ruModules = import.meta.glob('./locales/ru/*.json', { eager: true, import: 'default' }) as Record<string, Dict>;
const enModules = import.meta.glob('./locales/en/*.json', { eager: true, import: 'default' }) as Record<string, Dict>;

function mergeNamespaces(modules: Record<string, Dict>): Dict {
  const merged: Dict = {};
  for (const ns of Object.values(modules)) {
    Object.assign(merged, ns);
  }
  return merged;
}

const dictionaries: Record<Lang, Dict> = {
  'uz-Latn': mergeNamespaces(uzLatnModules),
  'uz-Cyrl': mergeNamespaces(uzCyrlModules),
  ru: mergeNamespaces(ruModules),
  en: mergeNamespaces(enModules),
};

const FALLBACK_CHAIN: Lang[] = ['uz-Latn', 'uz-Cyrl', 'ru', 'en'];

function isLang(v: string | null | undefined): v is Lang {
  return v === 'uz-Latn' || v === 'uz-Cyrl' || v === 'ru' || v === 'en';
}

function isoForLang(l: Lang): string {
  if (l === 'uz-Latn') return 'uz';
  if (l === 'uz-Cyrl') return 'uz-Cyrl';
  return l;
}

function readInitialLang(): Lang {
  try {
    const params = new URLSearchParams(window.location.search);
    const qp = params.get('lang');
    if (isLang(qp)) return qp;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLang(saved)) return saved;
  } catch {
    return DEFAULT_LANG;
  }
  return DEFAULT_LANG;
}

function interpolate(value: string, vars?: TVars): string {
  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, (match, name: string) => {
    const v = vars[name];
    return v === undefined || v === null ? match : String(v);
  });
}

function applyDocumentLang(l: Lang): void {
  try {
    document.documentElement.lang = isoForLang(l);
  } catch {
    void 0;
  }
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readInitialLang());

  useEffect(() => {
    applyDocumentLang(lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      void 0;
    }
    setLangState(next);
    applyDocumentLang(next);
  }, []);

  const t = useCallback(
    (key: string, vars?: TVars): string => {
      const primary = dictionaries[lang][key];
      if (primary !== undefined) return interpolate(primary, vars);
      for (const fb of FALLBACK_CHAIN) {
        if (fb === lang) continue;
        const val = dictionaries[fb][key];
        if (val !== undefined) return interpolate(val, vars);
      }
      return key;
    },
    [lang],
  );

  const value = useMemo<LangContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) {
    throw new Error('useLang must be used inside a <LangProvider>');
  }
  return ctx;
}
